import * as React from "react";
import {CardBoxSmall} from "../../../components/CardBox";
import {Title} from "../../../components/Title";
import {Legend, LegendItem, LegendLiElement} from "../../../components/charts/Legend";
import {ColorUtils} from "../../../components/Utils/color/ColorUtils";
import {Loader} from "../../../components/Utils/loader/Loader";
import {StatisticsUtils} from "../../../components/Utils/statistics/StatisticsUtils";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {CategoriesColors, MarketWidgetTexts, WidgetResponse} from "../../DashboardContainer";
import {DashboardRequestsData} from "../../data/DashboardData";
import {JSONMapper} from "../../data/JSONMapper";
import {PieChartWithLabels} from "../charts/PieChartWithLabels";
import {FormatLocaleNumber} from "../DashboardUtils";
import {AssetBaseDashboardWidget} from "./AssetBaseDashboardWidget";
import {LegendTooltip, LegendTooltipConfig} from "./LegendTooltip";
import {RestDataLoader} from "./RestDataLoader";
import "./style/Legend.scss";
import "./style/MarketWidget.scss";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {WidgetBadge, WidgetBadgeType} from "../DashboardWidget";
import AutoSizer from "react-virtualized-auto-sizer";

declare var AppTranslations: AppTranslations;

interface MarketWidgetProps {
    data: WidgetResponse;
    texts: MarketWidgetTexts;
    withTelemetryDevice: boolean;
    colorGenerator: ColorUtils;
}

interface WidgetState {
    ready: boolean;
    error: boolean;
    equipmentOnMarketCount: number;
    unauthorizedMovementAlarmEquipmentCount: number;
    registeredSites: number;
    unregisteredSites: number;
    equipmentByType: DashboardRequestsData.EquipmentByType[];
    selectedItemName: string;
    tooltipConfig: LegendTooltipConfig;
}

export enum SiteTypes {
    FACTORY = 3,
    MOBILE_SITE = 7,
    OFFICES = 6,
    OTHER = 4,
    POINT_OF_SALES = 1,
    UNREGISTERED_SITE = 5,
    WAREHOUSE = 2,
}

enum AlarmStatus {
    PENDING = 0,
    MANAGED = 1,
}

export enum Positions {
    MOBILE_LOCATION = "55fcec8c-c7bf-11e7-ad82-525400c5f801",
    OTHER = "55fce566-c7bf-11e7-ad82-525400c5f801",
    UNREGISTERED = "55fce912-c7bf-11e7-ad82-525400c5f801",
    POS = "55fa676e-c7bf-11e7-ad82-525400c5f801",
    OFFICE = "55fceaca-c7bf-11e7-ad82-525400c5f801",
}

export const REGISTERED_LOCATIONS_IDS = [
    SiteTypes.MOBILE_SITE,
    SiteTypes.OFFICES,
    SiteTypes.OTHER,
    SiteTypes.POINT_OF_SALES,
];

export const REGISTERED_LOCATIONS_GUIDS = [Positions.MOBILE_LOCATION, Positions.OTHER, Positions.POS, Positions.OFFICE];

const UNAUTHORIZED_MOVEMENT_ALARM_GUID = "ce73f8ae-5293-11e8-b9a0-525400c5f801";

export class MarketWidget extends React.Component<MarketWidgetProps, WidgetState> {
    constructor(props: MarketWidgetProps) {
        super(props);

        this.state = {
            ready: false,
            error: false,
            equipmentOnMarketCount: null,
            equipmentByType: null,
            selectedItemName: null,
            unauthorizedMovementAlarmEquipmentCount: null,
            registeredSites: null,
            unregisteredSites: null,
            tooltipConfig: {visible: false},
        };
        this.setSelectedItem = this.setSelectedItem.bind(this);
    }

    public setSelectedItem(itemName: string, e): void {
        const target: HTMLLIElement = e ? e.currentTarget : null;
        this.setState({selectedItemName: itemName}, () => {
            if (itemName !== null && itemName.indexOf(this.props.texts.others) > -1) {
                if (target) {
                    this.setState({
                        tooltipConfig: {
                            visible: true,
                            offSetX: target.offsetLeft,
                            offSetY: target.offsetTop + target.offsetHeight,
                            width: target.offsetWidth,
                            data: [],
                            father: target,
                        },
                    });
                }
            }
        });
    }

    public ready() {
        if (
            this.state.equipmentOnMarketCount != null
            && this.state.equipmentByType != null
            && this.state.unauthorizedMovementAlarmEquipmentCount != null
            && this.state.registeredSites != null
            && this.state.unregisteredSites != null
        ) {
            this.setState({ready: true});
        }
    }

    public componentDidMount() {
        RestDataLoader.loadData(
            "statistics/alarms/counting",
            {
                alarmTypeGuids: UNAUTHORIZED_MOVEMENT_ALARM_GUID,
                placements: "market",
                statuses: "pending",
                withTelemetry: this.props.withTelemetryDevice,
            },
            (data: any) => {
                this.setState({unauthorizedMovementAlarmEquipmentCount: data.count});
                this.ready();
            },
            () => this.setState({error: true}),
        );

        RestDataLoader.loadData(
            "statistics/equipment/counting/position",
            {
                placements: "market",
                withTelemetry: this.props.withTelemetryDevice,
            },
            (data) => {
                this.setState({
                    registeredSites: data.list
                        .filter((x) => REGISTERED_LOCATIONS_GUIDS.indexOf(x.key) > -1)
                        .reduce((a, x) => a + x.count, 0),
                    unregisteredSites: data.list
                        .filter((x) => x.key === Positions.UNREGISTERED)
                        .reduce((a, x) => a + x.count, 0),
                });
                this.ready();
            },
            () => this.setState({error: true}),
        );

        RestDataLoader.loadData(
            "statistics/equipment/counting/placement",
            {
                placements: "market",
                withTelemetry: this.props.withTelemetryDevice,
            },
            (data: any) => {
                this.setState({equipmentOnMarketCount: JSONMapper.getOnMarketCount(data.list)});
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
        RestDataLoader.loadData(
            "statistics/equipment/counting/type",
            {
                placements: "market",
                withTelemetry: this.props.withTelemetryDevice,
            },
            (data: any) => {
                this.setState({equipmentByType: JSONMapper.jsonEquipmentByTypeMapper(data.list)});
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
    }

    public render() {
        if (this.state.error) {
            return null;
        }
        let content = <Loader/>;
        if (!this.state.ready) {
            return (
                <div className="market-widget">
                    {content}
                </div>
            );
        }
        const equipmentByTypeData = this.state.equipmentByType.map(
            (item, i) => {
                return {
                    id: `${i}`,
                    code: item.typeGuid,
                    label: item.typeName,
                    frequency: item.equipmentCount,
                    icon: item.typeLogo,
                    color: item.typeColor || this.props.colorGenerator.getMappedColor(
                        CategoriesColors.COMPONENT_CATEGORIES,
                        item.typeGuid,
                    ),
                };
            });
        const preparedData = PieChartWithLabels.prepareData(
            equipmentByTypeData,
            6,
            "desc",
            {others: this.props.texts.others},
            undefined,
        );
        let total = 0;
        const legendItems: LegendItem[] = preparedData.map((pd) => {
            total += pd.frequency;
            return {
                color: pd.color,
                name: pd.label,
                value: pd.frequency,
                icon: pd.icon,
            };
        });
        legendItems.map((li) => li.percentage = StatisticsUtils.getPercentage(total, li.value));

        const onOtherTooltipClicked = (key) => () => this.onItemClick(key);
        const onOtherTooltipHover = (_, e) => null;

        content = (
            <VeBoxCard className="market-widget__container home-dashboard-card">
                <VeBoxCard.Header
                    className="market-widget__header commercial-header space-between"
                    title={this.props.texts.title}
                    titleClassName={"lowercase capitalize"}
                    back={
                        <WidgetBadge type={WidgetBadgeType.COMMERCIAL}/>
                    }
                />
                <VeBoxCard.Body className="market-widget__content">
                    <div className="cards-container">
                        <div className="top-card-container">
                            <CardBoxSmall
                                title={this.props.texts.equipmentOnMarket}
                                value={FormatLocaleNumber(this.state.equipmentOnMarketCount)}
                                bolder={true}
                                extended={true}
                                onClick={this.onCardClick(
                                    ...REGISTERED_LOCATIONS_IDS,
                                    SiteTypes.UNREGISTERED_SITE,
                                )}
                            />
                        </div>
                        <div className="bottom-cards-container">
                            <div className={"left-block"}>
                                <div className={"double-card-box"}>
                                    <CardBoxSmall
                                        title={this.props.texts.registered}
                                        value={AssetBaseDashboardWidget.getValueWithPercentage(
                                            this.state.registeredSites,
                                            this.state.equipmentOnMarketCount,
                                        )}
                                        extended={true}
                                        onClick={this.onCardClick(
                                            ...REGISTERED_LOCATIONS_IDS,
                                        )}
                                    />
                                    <CardBoxSmall
                                        title={this.props.texts.unauthorizedEquipmentMovementCount}
                                        value={AssetBaseDashboardWidget.getValueWithPercentage(
                                            this.state.unauthorizedMovementAlarmEquipmentCount,
                                            this.state.registeredSites,
                                        )}
                                        extended={true}
                                        onClick={this.onAlarmCardClick(
                                            ...REGISTERED_LOCATIONS_IDS,
                                            SiteTypes.UNREGISTERED_SITE,
                                        )}
                                    />
                                </div>
                            </div>
                            <CardBoxSmall
                                title={this.props.texts.unregistered}
                                value={AssetBaseDashboardWidget.getValueWithPercentage(
                                    this.state.unregisteredSites,
                                    this.state.equipmentOnMarketCount,
                                )}
                                extended={true}
                                onClick={this.onCardClick(
                                    SiteTypes.UNREGISTERED_SITE,
                                )}
                            />
                        </div>
                    </div>
                    <Title title={this.props.texts.equipmentByFamily}/>
                    <div className="chart-container">
                        <div className="pie-chart__legend__container">
                            <Legend
                                items={legendItems}
                                selectedItemName={this.state.selectedItemName}
                                onItemHover={this.setSelectedItem}
                                onClick={this.onItemClick}
                                iconOptions={{show: true}}
                                showValues={true}
                            />
                            <LegendTooltip
                                config={{
                                    ...this.state.tooltipConfig,
                                    data: equipmentByTypeData.filter((x, i) => i >= 5),
                                }}
                                className={"tooltip-wrapper"}
                                style={{
                                    color: "white",
                                }}
                            >
                                {(equipments: any[]) => (
                                    <div
                                        className={"tooltip-body"}
                                        style={{
                                            maxHeight: 200,
                                            overflow: "auto",
                                        }}
                                    >
                                        <ul className="legend">
                                            {equipments.map((equipment, i) => (
                                                <LegendLiElement
                                                    key={i}
                                                    item={{
                                                        name: equipment.label,
                                                        color: legendItems[legendItems.length - 1].color,
                                                        code: equipment.code,
                                                        icon: equipment.icon,
                                                        value: equipment.frequency,
                                                        percentage: StatisticsUtils.getPercentage(
                                                            total,
                                                            equipment.frequency,
                                                        ),
                                                    }}
                                                    iconOptions={{show: true}}
                                                    onClick={onOtherTooltipClicked(equipment.label)}
                                                    showValues={true}
                                                    liStyle={{
                                                        color: "white",
                                                    }}
                                                    onHover={onOtherTooltipHover}
                                                />
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </LegendTooltip>
                        </div>
                        <div className="pie-chart__graph">
                            <AutoSizer style={{width: "auto"}}>
                                {({width, height}) =>
                                    <PieChartWithLabels
                                        donutOptions={{activeDonut: true, showTotal: true}}
                                        width={width - 40}
                                        height={height}
                                        radiusRatio={2}
                                        data={preparedData}
                                        showPercentage={true}
                                        showIcon={false}
                                        noDataMessage={this.props.texts.noData}
                                        iconSize={{width: 30, height: 30}}
                                        labelLineDefaultLength={10}
                                        order={"desc"}
                                        selectedItem={this.state.selectedItemName}
                                        labelsOptions={{externalLegend: true}}
                                        onPathOver={this.setSelectedItem}
                                        onClick={this.onSliceClicked}
                                    />}
                            </AutoSizer>
                        </div>
                    </div>
                </VeBoxCard.Body>
            </VeBoxCard>
        );
        return (
            <div className="market-widget">
                {content}
            </div>
        );
    }

    private onItemClick = (key): void => {
        const result = this.state.equipmentByType.find((x) => x.typeName === key);
        (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
            sites: [
                ...REGISTERED_LOCATIONS_IDS,
                SiteTypes.UNREGISTERED_SITE],
            equipmentType: [result.typeGuid],
            allTelemetries: this.props.withTelemetryDevice,
            applyFilter: true,
        });
    }

    private onCardClick = (...sites: SiteTypes[]) => {
        return () => {
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                sites,
                allTelemetries: this.props.withTelemetryDevice,
                applyFilter: true,
            });
        };
    }

    private onAlarmCardClick = (...sites: SiteTypes[]) => {
        return () => {
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                sites,
                alarmStatuses: [AlarmStatus.PENDING],
                withoutAlarmTypes: false,
                alarmTypeGuids: [UNAUTHORIZED_MOVEMENT_ALARM_GUID],
                allTelemetries: this.props.withTelemetryDevice,
                applyFilter: true,
            });
        };
    }

    private onSliceClicked = (d) => {
        (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
            equipmentType: [d.data.code],
            sites: [
                ...REGISTERED_LOCATIONS_IDS,
                SiteTypes.UNREGISTERED_SITE],
            allTelemetries: this.props.withTelemetryDevice,
            applyFilter: true,
        });
    }
}
